import React from "react";
import "regenerator-runtime/runtime";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "./styles/reset.css";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { APIContextProvider } from "./utils/api";
import { UserInfoContextProvider } from "./utils/user";

import { AuthContextProvider } from "./utils/auth";
import Header from "./components/Layout/Header";
import Home from "./components/Home";
import About from "./components/About";
import Pricing from "./components/Pricing";

import ClientIntakeForm from "./components/ClientIntakeForm";
import Payment from "./components/Payment/Payment";
import ProtectedRoute from "./components/ProtectedRoute";
import Subscription from "./components/ClientPortal/Subscription";
import ClientIntakes from "./components/CoachPortal/ClientIntakes";
import LoginCallback from "./components/LoginCallback";
import MyWorkouts from "./components/ClientPortal/MyWorkouts";
import Coaching from "./components/CoachSignUp/Coaching";
import LogWorkout from "./components/ClientPortal/LogWorkout";
import Login from "./components/Login/Login";
import Clients from "./components/CoachPortal/Clients";
import SetPassword from "./components/Login/SetPassword";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Footer from "./components/Layout/Footer";
import Support from "./components/Support";
import ReviewClients from "./components/CoachPortal/ReviewClients";
import ContentLibrary from "./components/ContentLibrary/ContentLibrary";
import Messaging from "./components/CoachPortal/Messaging";
import ManageProducts from "./components/CoachPortal/ManageProducts";
import CoachProfileAdmin from "./components/CoachPortal/CoachProfileAdmin";
import PublicCoachProfile from "./components/CoachPortal/PublicCoachProfile";
import InstagramCallback from "./components/CoachPortal/InstagramCallback";
import CoachingConnectedAccountReturn from "./components/CoachSignUp/CoachingConnectedAccountReturn";
import CoachingConnectedAccountRefresh from "./components/CoachSignUp/CoachingConnectedAccountRefresh";
import CoachingSignUpCreateStripeAccount from "./components/CoachSignUp/CoachingSignUpCreateStripeAccount";
import CoachSignUpLanding from "./components/CoachSignUp/CoachSignUpLanding";
import CoachingSignUpCreateAccountLink from "./components/CoachSignUp/CoachingSignUpCreateAccountLink";
import CoachingSignUpComplete from "./components/CoachSignUp/CoachingSignUpComplete";
import WorkoutImport from "./components/CoachPortal/WorkoutImport";
import Templates from "./components/CoachPortal/Templates";
import Payouts from "./components/CoachPortal/Payouts";

const theme = createTheme({
  palette: {
    primary: {
      main: "#ff0000",
    },
    secondary: {
      main: "#ff0000",
    },
    info: {
      main: "#fff",
    },
  },
  typography: {
    fontFamily: ["Roboto Mono", "monospace"].join(","),
  },
});

const App = () => {
  return (
    <Router>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <AuthContextProvider>
          <APIContextProvider>
            <UserInfoContextProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Header />

                <Routes>
                  <Route path="/" exact element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/coaching" element={<Coaching />} />
                  <Route
                    path="/coaching/landing"
                    element={<CoachSignUpLanding />}
                  />
                  <Route
                    path="/coaching/create-account"
                    element={<CoachingSignUpCreateStripeAccount />}
                  />
                  <Route
                    path="/coaching/create-account-link"
                    element={<CoachingSignUpCreateAccountLink />}
                  />
                  <Route
                    path="/coaching/complete"
                    element={<CoachingSignUpComplete />}
                  />
                  <Route
                    path="/coaching/return/:accountId"
                    element={<CoachingConnectedAccountReturn />}
                  />
                  <Route
                    path="/coaching/refresh/:accountId"
                    element={<CoachingConnectedAccountRefresh />}
                  />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/support" element={<Support />} />
                  <Route
                    path="/client-intake/:encodedCoachId"
                    element={<ClientIntakeForm />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/set-password" element={<SetPassword />} />
                  <Route path="/workouts" element={<ProtectedRoute />}>
                    <Route path="/workouts" element={<MyWorkouts />} />
                    <Route
                      path="/workouts/:workoutId"
                      element={<LogWorkout />}
                    />
                  </Route>
                  <Route path="/clients" element={<ProtectedRoute />}>
                    <Route path="/clients" element={<Clients />} />
                  </Route>
                  <Route path="/client-intakes" element={<ProtectedRoute />}>
                    <Route path="/client-intakes" element={<ClientIntakes />} />
                  </Route>
                  <Route path="/client-activity" element={<ProtectedRoute />}>
                    <Route
                      path="/client-activity"
                      element={<ReviewClients />}
                    />
                  </Route>
                  <Route path="/templates" element={<ProtectedRoute />}>
                    <Route path="/templates" element={<Templates />} />
                  </Route>
                  <Route path="/messaging" element={<ProtectedRoute />}>
                    <Route path="/messaging" element={<Messaging />} />
                  </Route>
                  <Route path="/manage-products" element={<ProtectedRoute />}>
                    <Route
                      path="/manage-products"
                      element={<ManageProducts />}
                    />
                  </Route>
                  <Route path="/coach-profile" element={<ProtectedRoute />}>
                    <Route
                      path="/coach-profile"
                      element={<CoachProfileAdmin />}
                    />
                  </Route>
                  <Route
                    path="/coaches/:slug"
                    element={<PublicCoachProfile />}
                  />
                  <Route path="/instagram/callback" element={<InstagramCallback />} />
                  <Route path="/payment" element={<ProtectedRoute />}>
                    <Route path="/payment" element={<Payment />} />
                  </Route>
                  <Route path="/subscription" element={<ProtectedRoute />}>
                    <Route path="/subscription" element={<Subscription />} />
                  </Route>
                  <Route path="/callback" element={<LoginCallback />} />
                  <Route path="/content-library" element={<ProtectedRoute />}>
                    <Route
                      path="/content-library"
                      element={<ContentLibrary />}
                    />
                  </Route>
                  <Route path="/workout-import" element={<ProtectedRoute />}>
                    <Route path="/workout-import" element={<WorkoutImport />} />
                  </Route>
                  <Route path="/payouts" element={<ProtectedRoute />}>
                    <Route path="/payouts" element={<Payouts />} />
                  </Route>
                  <Route path="*" element={<Navigate to="" />} />
                </Routes>

                <Footer />
              </LocalizationProvider>
            </UserInfoContextProvider>
          </APIContextProvider>
        </AuthContextProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
